import React from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';

const EFFECTIVE_DATE = 'September 2, 2026';

function Section({ id, title, children }) {
  return (
    <section id={id} className="border-t border-white/8 py-8">
      <h2 className="text-2xl font-semibold tracking-tight text-stone-50">{title}</h2>
      <div className="mt-4 space-y-4 text-base leading-7 text-stone-300">{children}</div>
    </section>
  );
}

function PrivacyPage() {
  return (
    <div className="demo-shell min-h-screen overflow-hidden bg-stone-950 text-stone-100">
      <div className="demo-grid pointer-events-none fixed inset-0 opacity-60" aria-hidden="true" />
      <div className="demo-glow demo-glow-one" aria-hidden="true" />
      <div className="demo-glow demo-glow-two" aria-hidden="true" />

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col px-4 py-5 sm:px-8 sm:py-7 lg:px-10">
        <header className="flex items-center justify-between gap-4 border-b border-white/8 pb-5">
          <Link to="/" className="text-sm font-semibold uppercase tracking-[0.3em] text-amber-300">
            Zaphar
          </Link>
          <nav aria-label="Public navigation" className="flex items-center gap-5 text-sm text-stone-300">
            <Link to="/" className="transition hover:text-stone-50">Demo</Link>
          </nav>
        </header>

        <main className="flex-1">
          <PageMeta
            title="Zaphar Privacy Policy | Pevail LLC"
            description="Read how Pevail LLC handles account, receipt, transaction, connected financial, and technical data for Zaphar."
          />
          <section className="py-14 sm:py-20">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/85">Legal</p>
            <h1 className="mt-4 max-w-3xl text-4xl font-semibold tracking-tight text-stone-50 sm:text-6xl">
              Privacy Policy
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-stone-300 sm:text-lg">
              Effective {EFFECTIVE_DATE}. This Privacy Policy ("Policy") explains how Pevail LLC ("Pevail,"
              "we," "us," or "our") handles personal data in connection with the Zaphar application and
              related services (collectively, the "Service"). It applies to users of the Service worldwide
              ("you" or "User").
            </p>
          </section>

          <Section id="information-we-collect" title="1. Information we collect">
            <p>We may collect the following categories of personal data in connection with the Service:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li><strong>Account information:</strong> information used to create and manage an account, such as your name, email address, login credentials, and preferences.</li>
              <li><strong>Information you provide:</strong> receipts, images or files, transaction records, budgets, goals, lists, and other information you choose to enter or save in the Service.</li>
              <li><strong>Connected financial information:</strong> if you choose to connect a bank or card account, information made available through that connection, such as account details, balances, and transactions. We do not receive or store your online banking credentials.</li>
              <li><strong>Usage and technical information:</strong> information about how you use the Service, including device and browser information, IP address, approximate location, and similar technical information.</li>
              <li><strong>Communications:</strong> information you provide when you contact us, along with information needed to send account, security, and other Service-related messages.</li>
            </ul>
          </Section>

          <Section id="how-we-use-it" title="2. How we use information">
            <p>We may use personal data to:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Provide, operate, maintain, and improve the Service.</li>
              <li>Organize information you provide and offer features such as categorization, summaries, and spending insights.</li>
              <li>Support connected financial accounts, where that feature is available and you choose to use it.</li>
              <li>Send account, security, and Service-related communications.</li>
              <li>Protect the Service, our users, and others, including by detecting and addressing fraud, abuse, and security issues.</li>
              <li>Comply with applicable law and enforce our agreements.</li>
            </ul>
            <p>
              Some features may use automated tools to organize information or generate suggestions. These
              tools are intended to support the Service and are not used to make decisions about your legal
              rights or eligibility to use the Service.
            </p>
            <p>
              We do not sell or share personal data for cross-context behavioral advertising, and we do not
              use financial data to serve third-party advertising.
            </p>
          </Section>

          <Section id="cookies" title="3. Cookies and similar technologies">
            <p>
              We and the providers that support the Service may use cookies and similar technologies for
              account access, preferences, security, and basic analytics. You can control cookies through your
              browser settings, although disabling them may affect some functionality. These technologies are
              not used to serve third-party advertising.
            </p>
          </Section>

          <Section id="third-parties" title="4. When we share information">
            <p>
              We may share personal data with service providers that help us provide and protect the Service.
              These providers may support:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Financial account connections.</li>
              <li>Application hosting, cloud infrastructure, and data storage.</li>
              <li>Automated processing and Service features.</li>
              <li>Analytics, communications, security, and support.</li>
            </ul>
            <p>
              Service providers may process personal data only as needed to perform services for us and under
              appropriate restrictions. We may also disclose information when required by law, to protect the
              rights or safety of the Service and its users, or in connection with a merger, acquisition,
              financing, or sale of assets.
            </p>
          </Section>

          <Section id="data-retention" title="5. Data retention and deletion">
            <p>
              We retain account and financial information for as long as your account remains active or as
              needed to provide the Service. When you delete your account through the Service or request
              deletion in writing at{' '}
              <a href="mailto:privacy@zaphar.app" className="text-amber-200 underline decoration-amber-300/40 underline-offset-4 hover:text-amber-100">privacy@zaphar.app</a>{' '}
              , we will permanently delete your personal data, including receipts, transactions, and connected
              account information, within <strong>thirty (30) days</strong>, subject to the exceptions below.
            </p>
            <p>
              We may retain limited information when required by law, reasonably necessary to resolve a
              dispute, prevent fraud or abuse, protect security, enforce our agreements, or until it is removed
              through ordinary backup-retention processes.
            </p>
          </Section>

          <Section id="your-rights" title="6. Your rights">
            <p>
              Depending on where you live and subject to applicable law, you may have the right to request
              access to, correction of, deletion of, or a portable copy of your personal data. You may also be
              able to object to or restrict certain processing, withdraw consent where applicable, opt out of
              certain uses or disclosures, or complain to a privacy or supervisory authority.
            </p>
            <p>
              California residents may also have rights to know what personal data is collected and how it is
              used or disclosed, to correct or delete personal data, to opt out of its sale or sharing, and to
              limit certain uses of sensitive personal information.
            </p>
            <p>
              To exercise a privacy right, contact us at{' '}
              <a href="mailto:privacy@zaphar.app" className="text-amber-200 underline decoration-amber-300/40 underline-offset-4 hover:text-amber-100">privacy@zaphar.app</a>.
              We may take reasonable steps to verify your identity and will respond within the timeframe
              required by applicable law. We will not discriminate against you for exercising a privacy right.
            </p>
          </Section>

          <Section id="childrens-privacy" title="7. Children's privacy">
            <p>
              The Service is not directed to, and may not be used by, individuals under thirteen (13) years of
              age. We do not knowingly collect personal data from children under 13. If we learn that we have
              collected such information, we will delete it promptly. Please contact{' '}
              <a href="mailto:privacy@zaphar.app" className="text-amber-200 underline decoration-amber-300/40 underline-offset-4 hover:text-amber-100">privacy@zaphar.app</a>{' '}
              if you believe a child has provided personal data to us.
            </p>
          </Section>

          <Section id="security" title="8. Data security">
            <p>
              We use reasonable technical and organizational safeguards designed to protect personal data from
              unauthorized access, use, or disclosure. No method of transmission or storage is completely
              secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section id="international-transfers" title="9. International data transfers">
            <p>
              Personal data may be processed or stored in the United States and other jurisdictions. Where
              required by applicable law, we use appropriate safeguards for international data transfers.
            </p>
          </Section>

          <Section id="changes" title="10. Changes to this Policy">
            <p>
              We may update this Policy from time to time. We will update the effective date above and provide
              additional notice of material changes when required or appropriate.
            </p>
          </Section>

          <Section id="contact" title="11. Contact">
            <p>
              Pevail LLC<br />
              Attn: Privacy<br />
              Email:{' '}
              <a href="mailto:privacy@zaphar.app" className="text-amber-200 underline decoration-amber-300/40 underline-offset-4 hover:text-amber-100">privacy@zaphar.app</a>
            </p>
          </Section>
        </main>

        <footer className="mt-14 flex flex-col items-center gap-4 border-t border-white/8 pt-7">
          <div className="flex items-center gap-4 text-xs text-stone-500">
            <span>&copy; {new Date().getFullYear()} Pevail LLC</span>
            <Link to="/privacy" className="transition hover:text-stone-300">Privacy Policy</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default PrivacyPage;
